// The one registry of App limits. Every layer (company cell, collections, SDK,
// builder guide) reads its numbers here. test/app-limits-literal-scatter.mjs
// fails when a new power-of-two byte literal (16 KiB to 8 MiB, plain or as a
// KiB/MiB product) appears elsewhere in src/apps, src/app-data or
// src/contracts; it does not catch other numbers (row counts, timeouts), which
// belong here by convention. test/app-limits.mjs checks that the layers fit
// together end to end. Pure and browser-safe: no imports (the bridge contract,
// contracts/runtime-v2.mjs, reads its sizes from here).

const KiB = 1024;
const MiB = 1024 * KiB;

export const APP_LIMIT_UNITS = Object.freeze({ KiB, MiB });

/** Canonical saved source plus built output and runtime metadata. */
export const APP_SOURCE_BOUNDS = Object.freeze({
  files: 124,
  fileBytes: 256 * KiB,
  bytes: 8 * MiB,
});

/** Private workspace reads, independently bounded for source and output. */
export const APP_PREVIEW_FILES = Object.freeze({
  bytes: 8 * MiB,
  files: 128,
  directories: 128,
  depth: 16,
  singleFileBytes: 16 * MiB,
});

/** The frame/host bridge every browser request and reply crosses
 * (contracts/runtime-v2.mjs RUNTIME_LIMITS). Sized so a full read page, a
 * history page and one change's Undo each fit one message twice over. */
export const APP_BRIDGE = Object.freeze({
  bytes: 4 * MiB,
  nodes: 256 * KiB,
  depth: 12,
});

/** Company-cell connections. `ceiling` is the most one cell row may grant across
 * every process; each process role borrows its own share, and `web-overlap`
 * reserves the second web process of a zero-downtime swap. */
export const APP_CELL_POOL = Object.freeze({
  ceiling: 40,
  perCompany: 4,
  /** Registry shares give a company up to three connections while the share
   * has them free, so a burst of one App's reads runs three at a time. */
  defaultPerCompany: 3,
  /** Requests one process admits for a cell (running or queued), and for one
   * company; above either a request refuses with CELL_BUSY, which the SDK
   * retries after a pause. */
  pending: 256,
  perCompanyRequests: 128,
  /** An ordinary request waits this long for a busy share to free a connection. */
  waitMs: 10000,
  shares: Object.freeze({ web: 12, 'web-overlap': 12, runner: 6, workspace: 6 }),
});

/** Per-transaction settings for every company-cell transaction. The App role is
 * reached through SET LOCAL ROLE, so role-level settings never apply: each value
 * is set with set_config(..., true). temp_file_limit is superuser-only and lives
 * in the cell's RDS parameter group (platform/apps/cell/cell.tf);
 * test/app-limits.mjs checks that it is `tempFileLimitKiB`. */
export const APP_CELL_TRANSACTION = Object.freeze({
  statementTimeoutMs: 20000,
  lockTimeoutMs: 5000,
  idleInTransactionMs: 30000,
  workMem: '16MB',
  tempFileLimitKiB: 512 * KiB,
  /** Schema changes run on the migrator connection and may scan a table;
   * they wait longer for readers' short row locks than ordinary work does. */
  ddlStatementTimeoutMs: 120000,
  ddlLockTimeoutMs: 15000,
});

/** One stored row is its cells, never the columns of a version reading it. The
 * cells CHECK holds every path that writes rows (App writes and imports, Undo,
 * a Save's backfill, a cleared or restored copy) to `maxBytes`, and an App
 * write is measured the same way before it runs; the read window's first row
 * and the write review use the same number. Reading a stored row checks its
 * shape only, within `readBytes`: its cells, the reading version's columns and
 * a "not set" marker for each field it lacks always fit (test/app-limits.mjs),
 * so a row that can be stored can be read. */
export const APP_ROW = Object.freeze({ maxBytes: 256 * KiB, readBytes: APP_BRIDGE.bytes });

/** A collection declaration's canonical form (its digest input). */
export const APP_DECLARATION = Object.freeze({ maxBytes: MiB });

/** Physical read windows. A paged read stops at `rows` or `bytes`; a legacy
 * (cursorless) read must fit one complete page of `rows` (a query's limit is at
 * most APP_QUERY.maxLimit) and `legacyBytes`. */
export const APP_READ_PAGE = Object.freeze({
  rows: 1000,
  bytes: MiB,
  legacyBytes: MiB,
});

/** The structured query the browser SDK and server functions send. */
export const APP_QUERY = Object.freeze({
  maxLimit: 1000,
  fields: 200,
  filterNodes: 100,
  filterDepth: 12,
  inValues: 100,
  textLiteralChars: 64 * KiB,
  cursorChars: 4096,
  /** Sort values a cursor carries; longer values re-read the last row. */
  cursorKeyChars: 1024,
  inputBytes: 64 * KiB,
  sortKeys: 8,
  pathDepth: 8,
  searchChars: 1000,
  groupKeys: 4,
  aggregates: 16,
  maxGroups: 1000,
  /** A compiled query statement, below the transaction-wide timeout. */
  statementTimeoutMs: 10000,
});

/** One atomic collection write (browser, function or sync). */
/** One atomic collection write (browser, function or sync). Larger imports
 * and saves are chunked into batches this size, one receipt per chunk. A batch
 * stays below a server function's frame (256 KiB) with room for its envelope,
 * and its row versions fit the reviewed-write and receipt CHECKs (0001, 0003). */
export const APP_WRITE_BATCH = Object.freeze({
  rows: 500,
  bytes: 192 * KiB,
  /** The canonical command (batch plus scope and digest) around `bytes`. */
  commandBytes: 192 * KiB + 16 * KiB,
  /** Existing rows a batch locks and reviews; at least one maximum row. */
  reviewBytes: 8 * MiB,
  /** The changes one browser chunk carries, leaving room for the batch envelope. */
  chunkBytes: 176 * KiB,
});

/** History reads and Undo of one change (before and after of each row). */
/** History pages by change (newest first, a signed keyset cursor): at most
 * `maxLimit` changes and `bytes` per page; one change larger than a page refuses. */
export const APP_HISTORY = Object.freeze({
  defaultLimit: 50,
  maxLimit: 128,
  bytes: MiB,
});
export const APP_UNDO = Object.freeze({ rows: 500, bytes: 2 * MiB });

/** A stored operation receipt (0001 `_operation_receipts.result` CHECK). */
export const APP_RECEIPT = Object.freeze({ resultBytes: 256 * KiB });

/** The generated `app.records(id).load()` browser baseline. */
export const APP_RECORDS_LOAD = Object.freeze({ rows: 10000, bytes: 8 * MiB });

/** Server functions' edges the builder guide states: an outside response
 * (apps/egress), an inbound webhook body (apps/events) and one Tool Store
 * read's own arguments (apps/source-policy/read-plan.mjs). */
export const APP_FUNCTION_EDGES = Object.freeze({
  egressResponseBytes: 2 * MiB,
  webhookBodyBytes: 256 * KiB,
  toolArgumentBytes: 16 * KiB,
  /** The output run_app_function returns to the builder (apps/functions/run-controls.mjs). */
  builderRunOutputBytes: 16 * KiB,
});

/** Connected writes from App server functions (apps/functions/saved-tools.mjs):
 * calls per App, through the existing connected-write lane. */
export const APP_CONNECTED_WRITES = Object.freeze({ perMinute: 30, perDay: 1000 });
/** Row events (apps/events/row-events.mjs): how many one write, one event-run
 * function's writes, and one App per minute may queue (security M5). */
export const APP_ROW_EVENTS = Object.freeze({
  perWrite: 20,
  perSourceEvent: 50,
  perAppPerMinute: 120,
});
/** Host-side lines a function run adds to its run history (outside calls and
 * connected writes), beside the lines the function printed. */
export const APP_RUN_HOST_LINES = Object.freeze({ lines: 50, lineChars: 300 });

/** App job admission (apps/functions/job-admission.mjs): the most memory and
 * CPUs one process's App job budgets (PONEVA_CHAT_APP_JOB_MEMORY_BUDGET,
 * PONEVA_CHAT_APP_JOB_CPU_BUDGET) may be set to. The defaults and minimums
 * follow the function classes (apps/functions/limits.mjs). */
const GiB = 1024 * MiB;
export const APP_JOB_BUDGET = Object.freeze({ maxMemoryBytes: 64 * GiB, maxCpus: 64 });

/** Immutable per-version App files (apps/runtime/version-files.mjs): the
 * in-process cache and the largest single file served from it. */
export const APP_VERSION_FILES = Object.freeze({ cachedBytes: 64 * MiB, fileBytes: 4 * MiB });

/** Public Apps (apps/public): what one publication may copy and expose, and
 * how much anonymous traffic it may take. Rates are per minute or per day;
 * the per-IP read rate is per process (a fixed window in memory). */
export const APP_PUBLIC = Object.freeze({
  /** Collections one App exposes, and the rows and bytes copied from each. */
  collections: 8,
  snapshotRows: 5000,
  snapshotBytes: 4 * MiB,
  /** The built files copied at publish. */
  files: 512,
  fileBytes: APP_VERSION_FILES.fileBytes,
  filesBytes: 32 * MiB,
  /** Declared public actions and one submission. */
  actions: 8,
  actionFields: 32,
  actionBodyBytes: 16 * KiB,
  actionTextChars: 4000,
  actionsPerIpPerMinute: 5,
  /** A visitor (an IPv4 address or an IPv6 /64) spends at most 2% of either daily budget. */
  actionsPerIpPerDay: 20,
  opensPerIpPerDay: 1000,
  actionsPerAppPerMinute: 60,
  /** The publication's own daily budget. */
  actionsPerAppPerDay: 1000,
  opensPerAppPerDay: 50000,
  readsPerIpPerMinute: 600,
  /** Sites a publication may be embedded on. */
  embedOrigins: 20,
  /** How long a process trusts a publication row for files and data. */
  publicationCacheMs: 1000,
  cachedBytes: 64 * MiB,
});

/** Chat tool images (apps/runtime/chat-tool-images.mjs): a declared output
 * schema kept beside a result. */
export const APP_CHAT_IMAGES = Object.freeze({ outputSchemaBytes: 64 * KiB });

/** npm packages an App adds through Chat's package firewall (apps/packages):
 * the minimum release age, how much one request, package and App may hold,
 * and the bounds on what the registry may send. */
export const APP_PACKAGES = Object.freeze({
  minReleaseAgeMs: 24 * 60 * 60 * 1000,
  requests: 8,
  packages: 256,
  depth: 32,
  packumentBytes: 48 * MiB,
  tarballBytes: 16 * MiB,
  unpackedBytes: 96 * MiB,
  totalBytes: 64 * MiB,
  lockfileBytes: 2 * MiB,
  /** The App build workspace's package quota: real unpacked bytes and files,
   * measured while extracting (a package's own size claim is not trusted). */
  workspaceBytes: 256 * MiB,
  workspaceFiles: 60000,
  fetchTimeoutMs: 30000,
  parallel: 8,
  /** What one server function's bundle may unpack and read: the packages its
   * imports reach, inside the function sandbox's 64 MiB /tmp. */
  functionArchiveBytes: 16 * MiB,
  functionUnpackedBytes: 40 * MiB,
  functionFiles: 20000,
});

/** The browser SDK's refresh helper (apps/tracer/seed-sdk.mjs): an App's
 * repeated reload runs at most this often. */
export const APP_REFRESH = Object.freeze({ minIntervalMs: 15000 });

/** Destructive table changes (app-data/cell/table-change.mjs): the copy taken
 * just before dropping a field's values or clearing values that break a rule
 * holds at most `copyBytes` of those values and is kept `retainDays` (a dropped
 * table is moved aside whole, never copied, and kept as long); a preview shows
 * up to `sampleRows` rows, each value cut to `sampleChars`. */
export const APP_TABLE_COPY = Object.freeze({
  /** A Save names at most this many fields and tables it hid that still hold values. */
  listedHidden: 20,
  copyBytes: 64 * MiB,
  retainDays: 30,
  sampleRows: 5,
  sampleChars: 200,
});

/** Reply budgets larger than one bridge message. Each would be valid on the
 * server but refused in the browser; test/app-limits.mjs fails until this list
 * matches. Empty since the bridge carries 4 MiB. */
export const APP_KNOWN_BRIDGE_GAPS = Object.freeze([]);
